import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
    CheckCircle2, ArrowLeft, ShieldCheck, CreditCard,
    Loader2, XCircle, PartyPopper
} from 'lucide-react';
import { getPublicPricingPlans, createPublicCheckoutOrder, verifyRazorpayPayment } from '../../lib/api';

// ── helpers ────────────────────────────────────────────────────────────────────

/** Dynamically inject the Razorpay checkout.js script */
function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (document.getElementById('razorpay-script')) {
            resolve(true);
            return;
        }
        const script = document.createElement('script');
        script.id = 'razorpay-script';
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

/** Convert a numeric value from the API into paise (integer) */
function toPaise(value) {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return 0;
    return Math.round(num * 100);
}

/** Format a numeric value for display e.g. 1499 → "₹1,499" */
function formatINR(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return `₹${num.toLocaleString('en-IN')}`;
}

/** Normalise the plan key from the URL query: "dsa-core-pro" → "dsa core pro" */
function normalisePlanKey(raw = '') {
    return raw.toLowerCase().replace(/-/g, ' ');
}

// ── component ──────────────────────────────────────────────────────────────────

const Checkout = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const planKey = searchParams.get('plan') || '';
    const billing = searchParams.get('billing') || 'monthly';

    // ── state ──────────────────────────────────────────────────────────────────

    const [planLoading, setPlanLoading] = useState(true);
    const [planError, setPlanError] = useState('');
    const [plan, setPlan] = useState(null);   // raw API plan object

    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', mobile: '', companyName: '',
    });

    /** 'idle' | 'paying' | 'success' | 'failed' */
    const [payStatus, setPayStatus] = useState('idle');
    const [payError, setPayError] = useState('');
    const [emailStatus, setEmailStatus] = useState(null);

    // ── fetch plan from API ─────────────────────────────────────────────────────

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const resp = await getPublicPricingPlans();
                const raw = Array.isArray(resp?.data) ? resp.data
                    : Array.isArray(resp?.data?.items) ? resp.data.items : [];

                if (!mounted) return;

                if (raw.length === 0) {
                    setPlanError('No pricing plans available. Please contact support.');
                    return;
                }

                // Match by normalised name or fall back to first plan
                const normKey = normalisePlanKey(planKey);
                const matched =
                    raw.find(p => normalisePlanKey(p.name) === normKey) || raw[0];

                setPlan(matched);
            } catch (err) {
                if (mounted) setPlanError(err?.message || 'Failed to load plan details.');
            } finally {
                if (mounted) setPlanLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [planKey]);

    // ── derived display values ─────────────────────────────────────────────────

    const displayPrice = plan
        ? (billing === 'yearly'
            ? formatINR(plan.yearly_price ?? plan.monthly_price * 12)
            : formatINR(plan.monthly_price))
        : '—';

    const amountPaise = plan
        ? (billing === 'yearly'
            ? toPaise(plan.yearly_price ?? plan.monthly_price * 12)
            : toPaise(plan.monthly_price))
        : 0;

    // ── handlers ───────────────────────────────────────────────────────────────

    const handleChange = (e) =>
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setPayStatus('paying');
        setPayError('');

        try {
            // 1. Load Razorpay SDK
            const loaded = await loadRazorpayScript();
            if (!loaded) throw new Error('Failed to load payment gateway. Check your internet connection.');

            // 2. Create order on backend
            const orderResp = await createPublicCheckoutOrder({
                plan_id: plan?.id,
                billing_cycle: billing,
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
                mobile: formData.mobile,
                company_name: formData.companyName,
            });
            if (!orderResp?.success || !orderResp?.data?.order_id) {
                throw new Error('Could not initiate payment. Please try again.');
            }

            const { order_id, amount, currency, key_id } = orderResp.data;
            const razorpayKey = key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;

            // 3. Open Razorpay checkout modal
            const options = {
                key: razorpayKey,
                amount: String(amount),
                currency: currency || 'INR',
                name: 'FinMatrixRQ',
                description: `${plan?.name || 'Plan'} — ${billing} subscription`,
                order_id,
                prefill: {
                    name: `${formData.firstName} ${formData.lastName}`.trim(),
                    email: formData.email,
                    contact: formData.mobile,
                },
                theme: { color: '#059669' },  // emerald-600
                modal: {
                    ondismiss: () => {
                        // User closed without paying
                        setPayStatus('idle');
                    },
                },
                handler: async (response) => {
                    // ✅ Verify the payment server-side BEFORE trusting it
                    // This prevents anyone from faking success via browser console
                    try {
                        const verifyResp = await verifyRazorpayPayment(
                            response.razorpay_order_id,
                            response.razorpay_payment_id,
                            response.razorpay_signature,
                        );
                        // Only reach here if HMAC-SHA256 signature matched on the server
                        if (!verifyResp?.success) {
                            throw new Error('Payment verification failed.');
                        }
                        setEmailStatus(Boolean(verifyResp?.data?.email_sent));
                        setPayStatus('success');
                    } catch (verifyErr) {
                        setPayError(
                            verifyErr?.message || 'Payment could not be verified. Please contact support.'
                        );
                        setPayStatus('failed');
                    }
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (response) => {
                setPayError(
                    response?.error?.description || 'Payment failed. Please try again.'
                );
                setPayStatus('failed');
            });
            rzp.open();

        } catch (err) {
            setPayError(err?.message || 'Something went wrong. Please try again.');
            setPayStatus('failed');
        }
    };

    // ── render: loading ────────────────────────────────────────────────────────

    if (planLoading) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-slate-500 dark:text-slate-400">
                    <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
                    <p className="text-sm font-medium">Loading plan details…</p>
                </div>
            </div>
        );
    }

    if (planError) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center px-4">
                <div className="text-center max-w-md">
                    <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Something went wrong</h2>
                    <p className="text-slate-500 dark:text-slate-400 mb-6">{planError}</p>
                    <Link to="/" className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-500">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
                    </Link>
                </div>
            </div>
        );
    }

    // ── render: payment success ────────────────────────────────────────────────

    if (payStatus === 'success') {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center px-4">
                <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-12 max-w-md w-full text-center border border-slate-100 dark:border-slate-700">
                    <div className="h-20 w-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6">
                        <PartyPopper className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-3 font-['Plus_Jakarta_Sans']">
                        Payment Successful! 🎉
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 mb-2">
                        Welcome to <span className="font-bold text-slate-700 dark:text-slate-200">{plan?.name}</span>!
                    </p>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-8">
                        {emailStatus
                            ? (
                                <>
                                    A confirmation email has been sent to <span className="font-semibold">{formData.email}</span>.
                                    Your workspace has been activated. Check your email for login details and next steps.
                                </>
                            )
                            : (
                                <>
                                    Your workspace has been activated, but the confirmation email could not be sent yet.
                                    Please contact <span className="font-semibold">info@finmatrixrq.com</span> if you do not receive it.
                                </>
                            )}
                    </p>
                    <div className="space-y-3">
                        <Link to="/">
                            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 font-bold">
                                Back to Home
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // ── render: main checkout ──────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <Link to="/" className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-500 transition-colors">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Home
                    </Link>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-slate-100 dark:border-slate-800">
                    {/* Left Side: Order Summary */}
                    <div className="md:w-1/3 bg-slate-900 text-white p-8 md:p-12 flex flex-col justify-between">
                        <div>
                            <h2 className="text-xl font-bold mb-8 text-emerald-400 flex items-center gap-2">
                                Order Summary
                            </h2>
                            <div className="space-y-6">
                                <div className="pb-4 border-b border-slate-700/50">
                                    <p className="text-sm text-slate-400 mb-1">Selected Plan</p>
                                    <p className="text-2xl font-black font-['Plus_Jakarta_Sans']">
                                        {String(plan?.name || '').toUpperCase()}
                                    </p>
                                </div>
                                <div className="pb-4 border-b border-slate-700/50">
                                    <p className="text-sm text-slate-400 mb-1">Billing Cycle</p>
                                    <p className="text-lg font-semibold capitalize">{billing}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-400 mb-2">Total Amount</p>
                                    <p className="text-4xl font-extrabold text-emerald-400 bg-emerald-500/10 inline-block px-4 py-2 rounded-xl">
                                        {displayPrice}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 space-y-4">
                            <div className="flex items-center gap-3 text-sm text-slate-300">
                                <ShieldCheck className="h-5 w-5 text-emerald-500" />
                                <span>Secure 256-bit SSL Checkout</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-300">
                                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                                <span>30 Day Money-Back Guarantee</span>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Checkout Form */}
                    <div className="md:w-2/3 p-8 md:p-12">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Checkout Details</h2>
                        <p className="text-slate-500 dark:text-slate-400 mb-8">
                            Please enter your details to complete the purchase and create your account.
                        </p>

                        {/* Payment failed error banner */}
                        {payStatus === 'failed' && payError && (
                            <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4">
                                <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                                <p className="text-sm text-red-700 dark:text-red-400">{payError}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                        placeholder="Enter first name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                        placeholder="Enter last name"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                    placeholder="name@company.com"
                                />
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="mobile" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Mobile Number
                                </label>
                                <input
                                    type="tel"
                                    id="mobile"
                                    name="mobile"
                                    required
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                    placeholder="+91 98765 43210"
                                />
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="companyName" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Company / Brand Name
                                </label>
                                <input
                                    type="text"
                                    id="companyName"
                                    name="companyName"
                                    required
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                    placeholder="Your company or brand"
                                />
                            </div>

                            <div className="pt-6 mt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button
                                    type="submit"
                                    size="lg"
                                    disabled={payStatus === 'paying' || amountPaise === 0}
                                    className="w-full h-14 text-lg font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {payStatus === 'paying' ? (
                                        <>
                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                            Opening payment…
                                        </>
                                    ) : (
                                        <>
                                            <CreditCard className="mr-2 h-5 w-5" />
                                            Pay {displayPrice} via Razorpay
                                        </>
                                    )}
                                </Button>
                                <p className="text-center text-xs text-slate-500 mt-4">
                                    By proceeding, you agree to our Terms of Service and Privacy Policy.
                                    Payments are securely processed via Razorpay.
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
